import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthGuard } from 'src/auth/guard';
import { PrivilegeType } from 'src/services/role';
import DashboardLayout from 'src/layouts/dashboard';

import Restricted from 'src/components/restricted';
import { LoadingScreen } from 'src/components/loading-screen';

import { ActionPlanListView } from 'src/sections/actionPlan/view';
import ActionPlanCalendarView from 'src/sections/actionPlan/view/action-plan-calendar';

import { paths } from '../paths';

// ----------------------------------------------------------------------
// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));

// Objectives Management

const PlanManagementPage = lazy(
  () => import('src/pages/dashboard/objectivesManagement/planManagement')
);
const PlanEditorPage = lazy(() => import('src/pages/dashboard/objectivesManagement/planEditor'));
const PlanDetail = lazy(() => import('src/sections/objectivesManagement/view/detail'));
const ProjectManagementPage = lazy(() => import('src/pages/dashboard/projectManagement'));
const ProjectManagementNewPage = lazy(() => import('src/pages/dashboard/projectManagement/new'));
const ProjectManagementEditPage = lazy(() => import('src/pages/dashboard/projectManagement/edit'));

const ProjectManagementDetailPage = lazy(
  () => import('src/pages/dashboard/projectManagement/detail')
);

// Online Trading
const InternalTradingPage = lazy(() => import('src/pages/dashboard/onlineTrading/internalTrading'));
const OperationalStatementTransactionPage = lazy(
  () => import('src/pages/dashboard/onlineTrading/operationalStatementTransaction')
);

// ASSESSMENT DIMENSION
const AssessmentDimensionListPage = lazy(
  () => import('src/pages/dashboard/assessmentDimension/list')
);

// SUBJECT
const SubjectListPage = lazy(() => import('src/pages/dashboard/subject/list'));

// CURRENCY CONVERSION
const CurrencyConversionListPage = lazy(
  () => import('src/pages/dashboard/currencyConversion/list')
);

// REPORT TEMPLATE
const ReportTemplatePage = lazy(
  () => import('src/pages/dashboard/systemManagement/reportTemplate')
);
const ReportTemplateCreatePage = lazy(
  () => import('src/pages/dashboard/systemManagement/reportTemplateCreate')
);
const ReportTemplateEditPage = lazy(
  () => import('src/pages/dashboard/systemManagement/reportTemplateEdit')
);

const StatementIncomeListPage = lazy(() => import('src/pages/dashboard/analysis/income-list'));
const StatementExpenseListPage = lazy(() => import('src/pages/dashboard/analysis/expense-list'));
const TargetDifferencePage = lazy(() => import('src/pages/dashboard/analysis/target-difference'));
const TargetDifferenceDetail = lazy(
  () => import('src/pages/dashboard/analysis/target-difference-detail')
);
const TargetDifferenceDetailEdit = lazy(
  () => import('src/pages/dashboard/analysis/target-difference-detail-edit')
);
const AnalysisReportPage = lazy(() => import('src/pages/dashboard/analysis/analysis-report'));
const BusinessPlanReportPage = lazy(
  () => import('src/pages/dashboard/objectivesManagement/business-plan-report')
);
const ExecutionRatePage = lazy(
  () => import('src/pages/dashboard/objectivesManagement/execution-rate')
);

// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));

// 改善提案
const ImprovementProposalPage = lazy(() => import('src/pages/dashboard/improvementProposal'));
const ImprovementProposalNewPage = lazy(
  () => import('src/pages/dashboard/improvementProposal/new')
);
const ImprovementProposalDetailPage = lazy(
  () => import('src/pages/dashboard/improvementProposal/detail')
);
const ImprovementProposalEditPage = lazy(
  () => import('src/pages/dashboard/improvementProposal/edit')
);
const ImprovementCooperationPage = lazy(
  () => import('src/pages/dashboard/improvementProposal/cooperation')
);

// TRADE CATEGORY
const TradeCategoryListPage = lazy(() => import('src/pages/dashboard/tradeCategory/list'));

// KPI REPORT
const KpiReportPage = lazy(() => import('src/pages/dashboard/objectivesManagement/kpi-report'));

// disassemble
const BusinessPlanDisassemblePage = lazy(
  () => import('src/pages/dashboard/objectivesManagement/businessPlanDisassemble')
);
const BudgetPlanDisassemblePage = lazy(
  () => import('src/pages/dashboard/objectivesManagement/budgetPlanDisassemble')
);
// ----------------------------------------------------------------------
export const routerMap = [
  {
    label: '首页',
    key: paths.dashboard.root,
    children: <IndexPage />,
  },
  {
    label: '目标拆解',
    key: paths.dashboard.objectivesManagement.planManagement.root,
    children: (
      <Restricted type={PrivilegeType.PLAN}>
        <PlanManagementPage />
      </Restricted>
    ),
  },

  {
    label: '编辑目标拆解',
    key: paths.dashboard.objectivesManagement.planManagement.edit,
    children: (
      <Restricted requireWrite type={PrivilegeType.PLAN}>
        <PlanEditorPage allowBack action="edit" />
      </Restricted>
    ),
  },
  {
    label: '目标拆解详情',
    key: paths.dashboard.objectivesManagement.planManagement.detail,
    children: (
      <Restricted type={PrivilegeType.PLAN}>
        <PlanDetail />
      </Restricted>
    ),
  },
  {
    label: '年度计划拆解',
    key: paths.dashboard.objectivesManagement.planManagement.businessPlanDisassemble,
    children: (
      <Restricted type={PrivilegeType.PLAN}>
        <BusinessPlanDisassemblePage />
      </Restricted>
    ),
  },
  {
    label: '经营会计目标拆解',
    key: paths.dashboard.objectivesManagement.planManagement.budgetPlanDisassemble,
    children: (
      <Restricted type={PrivilegeType.PLAN}>
        <BudgetPlanDisassemblePage />
      </Restricted>
    ),
  },
  {
    label: '项目(策略)管理',
    key: paths.dashboard.objectivesManagement.projectManagement.root,
    children: (
      <Restricted type={PrivilegeType.PROJECT}>
        <ProjectManagementPage />
      </Restricted>
    ),
  },

  {
    label: '编辑项目(策略)管理',
    key: paths.dashboard.objectivesManagement.projectManagement.edit,
    children: (
      <Restricted requireWrite type={PrivilegeType.PROJECT}>
        <ProjectManagementEditPage />
      </Restricted>
    ),
  },
  {
    label: '项目(策略)管理详情',
    key: paths.dashboard.objectivesManagement.projectManagement.detail,
    children: (
      <Restricted type={PrivilegeType.PROJECT}>
        <ProjectManagementDetailPage />
      </Restricted>
    ),
  },
  {
    label: '年度计划表',
    key: paths.dashboard.objectivesManagement.businessPlanReport.root,
    children: (
      <Restricted type={PrivilegeType.PLAN}>
        <BusinessPlanReportPage />
      </Restricted>
    ),
  },
  {
    label: '指标年度计划表',
    key: paths.dashboard.objectivesManagement.kpiReport.root,
    children: (
      <Restricted type={PrivilegeType.PLAN}>
        <KpiReportPage />
      </Restricted>
    ),
  },
  {
    label: '内部交易',
    key: paths.dashboard.onlineTrading.internalTrading.root,
    children: (
      <Restricted type={PrivilegeType.INTERNAL_TRADE}>
        <InternalTradingPage />
      </Restricted>
    ),
  },
  {
    label: '内部交易-编辑',
    key: paths.dashboard.onlineTrading.internalTrading.edit,
    children: (
      <Restricted type={PrivilegeType.INTERNAL_TRADE}>
        <InternalTradingPage />
      </Restricted>
    ),
  },
  {
    label: '内部交易-详情',
    key: paths.dashboard.onlineTrading.internalTrading.detail,
    children: (
      <Restricted type={PrivilegeType.INTERNAL_TRADE}>
        <InternalTradingPage />
      </Restricted>
    ),
  },
  {
    label: '内部交易-审批',
    key: paths.dashboard.onlineTrading.internalTrading.audit,
    children: (
      <Restricted type={PrivilegeType.INTERNAL_TRADE}>
        <InternalTradingPage />
      </Restricted>
    ),
  },
  {
    label: '订单数据流水',
    key: paths.dashboard.onlineTrading.operationalStatementTransaction.root,
    children: (
      <Restricted type={PrivilegeType.OPERATIONAL_STATEMENT_TRANSACTION}>
        <OperationalStatementTransactionPage />
      </Restricted>
    ),
  },
  {
    label: '报表收入明细',
    key: paths.dashboard.analysis.statementIncomeList,
    children: (
      <Restricted type={PrivilegeType.INCOME_ANALYSIS}>
        <StatementIncomeListPage />
      </Restricted>
    ),
  },
  {
    label: '报表支出明细',
    key: paths.dashboard.analysis.statementExpenseList,
    children: (
      <Restricted type={PrivilegeType.EXPENSE_ANALYSIS}>
        <StatementExpenseListPage />
      </Restricted>
    ),
  },
  {
    label: '目标差异分析',
    key: paths.dashboard.analysis.targetDifferenceList,
    children: (
      <Restricted type={PrivilegeType.INCOME_ANALYSIS}>
        <TargetDifferencePage />
      </Restricted>
    ),
  },
  {
    label: '目标差异分析详情',
    key: paths.dashboard.analysis.targetDifference.detail,
    children: (
      <Restricted type={PrivilegeType.INCOME_ANALYSIS}>
        <TargetDifferenceDetail />
      </Restricted>
    ),
  },
  {
    label: '编辑目标差异分析',
    key: paths.dashboard.analysis.targetDifference.edit,
    children: (
      <Restricted type={PrivilegeType.INCOME_ANALYSIS}>
        <TargetDifferenceDetailEdit />
      </Restricted>
    ),
  },
  {
    label: '经营会计报表',
    key: paths.dashboard.analysis.analysisReportList,
    children: (
      <Restricted type={PrivilegeType.INCOME_ANALYSIS}>
        <AnalysisReportPage />
      </Restricted>
    ),
  },
  {
    label: '改善提案',
    key: paths.dashboard.improvementProposal.root,
    children: (
      <Restricted type={PrivilegeType.PROPOSAL}>
        <ImprovementProposalPage />
      </Restricted>
    ),
  },
  {
    label: '新建改善提案',
    key: paths.dashboard.improvementProposal.new,
    children: (
      <Restricted requireWrite type={PrivilegeType.PROPOSAL}>
        <ImprovementProposalNewPage />
      </Restricted>
    ),
  },
  {
    label: '编辑改善提案',
    key: paths.dashboard.improvementProposal.edit,
    children: (
      <Restricted requireWrite type={PrivilegeType.PROPOSAL}>
        <ImprovementProposalEditPage />
      </Restricted>
    ),
  },
  {
    label: '改善提案详情',
    key: paths.dashboard.improvementProposal.detail,
    children: (
      <Restricted type={PrivilegeType.PROPOSAL}>
        <ImprovementProposalDetailPage />
      </Restricted>
    ),
  },
  {
    label: '行动计划管理',
    key: paths.dashboard.improvementProposal.actionPlanManagement.root,
    children: (
      <Restricted type={PrivilegeType.ACTION_PLAN}>
        <ActionPlanListView />
      </Restricted>
    ),
  },
  {
    label: '执行率',
    key: paths.dashboard.improvementProposal.executionRate.root,
    children: (
      <Restricted type={PrivilegeType.PLAN}>
        <ExecutionRatePage />
      </Restricted>
    ),
  },
  {
    label: '支援配合',
    key: paths.dashboard.improvementProposal.cooperation.root,
    children: (
      <Restricted type={PrivilegeType.SUPPORT_REQUEST}>
        <ImprovementCooperationPage />
      </Restricted>
    ),
  },
  {
    label: '考核维度',
    key: paths.dashboard.systemManagement.assessmentDimension.root,
    children: (
      <Restricted type={[PrivilegeType.ASSESSMENT_DIMENSION]}>
        <AssessmentDimensionListPage />
      </Restricted>
    ),
  },
  {
    label: '考核科目',
    key: paths.dashboard.systemManagement.subject.root,
    children: (
      <Restricted type={[PrivilegeType.SUBJECT]}>
        <SubjectListPage />
      </Restricted>
    ),
  },
  {
    label: '汇率管理',
    key: paths.dashboard.systemManagement.currencyConversion.root,
    children: (
      <Restricted type={[PrivilegeType.CURRENCY_CONVERSION]}>
        <CurrencyConversionListPage />
      </Restricted>
    ),
  },
  {
    label: '报表模版',
    key: paths.dashboard.systemManagement.reportTemplate.root,
    children: (
      <Restricted type={[PrivilegeType.TEMPLATE]}>
        <ReportTemplatePage />
      </Restricted>
    ),
  },
  {
    label: '新建模版',
    key: paths.dashboard.systemManagement.reportTemplate.new,
    children: (
      <Restricted type={[PrivilegeType.TEMPLATE]}>
        <ReportTemplateCreatePage />
      </Restricted>
    ),
  },
  {
    label: '编辑模版',
    key: paths.dashboard.systemManagement.reportTemplate.edit,
    children: (
      <Restricted type={[PrivilegeType.TEMPLATE]}>
        <ReportTemplateEditPage />
      </Restricted>
    ),
  },
  {
    label: '交易类型管理',
    key: paths.dashboard.systemManagement.tradeCategory.root,
    children: (
      <Restricted type={[PrivilegeType.TRADE_CATEGORY]}>
        <TradeCategoryListPage />
      </Restricted>
    ),
  },
];
export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      {
        path: 'objectivesManagement',
        children: [
          {
            path: 'planManagement',
            children: [
              {
                path: 'new',
                element: (
                  <Restricted requireWrite type={PrivilegeType.PLAN}>
                    <PlanEditorPage allowBack action="create" />
                  </Restricted>
                ),
              },
              {
                path: 'edit',
              },
              {
                path: 'detail',
              },
              {
                path: 'businessPlanDisassemble',
                element: (
                  <Restricted type={PrivilegeType.PLAN}>
                    <BusinessPlanDisassemblePage />
                  </Restricted>
                ),
              },
              {
                path: 'budgetPlanDisassemble',
                element: (
                  <Restricted type={PrivilegeType.PLAN}>
                    <BudgetPlanDisassemblePage />
                  </Restricted>
                ),
              },
            ],
          },
          {
            path: 'projectManagement',
            children: [
              {
                path: 'new',
                element: (
                  <Restricted requireWrite type={PrivilegeType.PROJECT}>
                    <ProjectManagementNewPage />
                  </Restricted>
                ),
              },
              {
                path: 'edit',
              },
              {
                path: 'detail',
              },
              {
                path: 'disassemble',
              },
            ],
          },
          {
            path: 'business-plan-report',
            children: [
              {
                path: 'list',
              },
            ],
          },

          {
            path: 'kpi-report',
            children: [
              {
                path: 'list',
              },
            ],
          },
        ],
      },
      {
        path: 'systemManagement',
        children: [
          {
            path: 'assessmentDimension',
          },
          {
            path: 'subject',
          },
          {
            path: 'currencyConversion',
          },
          {
            path: 'reportTemplate',
            children: [
              {
                path: 'new',
              },
              {
                path: 'edit',
              },
            ],
          },
          {
            path: 'tradeCategory',
          },
        ],
      },
      {
        path: 'analysis',
        children: [
          {
            path: 'statement-income',
            children: [
              {
                path: 'list',
              },
            ],
          },
          {
            path: 'statement-expense',
            children: [
              {
                path: 'list',
              },
            ],
          },
          {
            path: 'target-difference',
            children: [
              {
                path: 'list',
              },
              {
                path: 'detail',
              },
              {
                path: 'edit',
              },
            ],
          },
          {
            path: 'analysis-report',
            children: [
              {
                path: 'list',
              },
            ],
          },
        ],
      },
      {
        path: 'onlineTrading',
        children: [
          {
            path: 'internalTrading',
            children: [
              {
                index: true,
              },
              {
                path: 'edit',
              },
              {
                path: 'detail',
              },
              {
                path: 'audit',
              },
            ],
          },
          {
            path: 'operationalStatementTransaction',
          },
        ],
      },
      {
        path: 'improvementProposal',
        children: [
          {
            path: 'new',
          },
          {
            path: 'edit',
          },
          {
            path: 'detail',
          },
          {
            path: 'cooperation',
          },
          {
            path: 'actionPlanManagement',
            children: [
              {
                path: ':id/edit',
                element: (
                  <Restricted requireWrite type={PrivilegeType.ACTION_PLAN}>
                    <ActionPlanListView />
                  </Restricted>
                ),
              },
              {
                path: ':id',
                element: (
                  <Restricted type={PrivilegeType.ACTION_PLAN}>
                    <ActionPlanListView />
                  </Restricted>
                ),
              },
              {
                path: 'new',
                element: (
                  <Restricted requireWrite type={PrivilegeType.ACTION_PLAN}>
                    <ActionPlanListView />
                  </Restricted>
                ),
              },
            ],
          },
          {
            path: 'execution-rate',
            children: [
              {
                path: 'list',
                element: (
                  <Restricted type={PrivilegeType.ADMIN}>
                    <ExecutionRatePage />
                  </Restricted>
                ),
              },
            ],
          },
        ],
      },
      { path: 'permission', element: <PermissionDeniedPage /> },
      {
        path: 'action-plan',
        children: [
          {
            element: <ActionPlanListView />,
            index: true,
          },
          {
            path: 'calendar',
            element: <ActionPlanCalendarView />,
          },
        ],
      },
    ],
  },
];
